import React, { createContext, useContext, ReactNode } from "react";
import { useQuery } from "convex/react";
import posthog from "posthog-js";
import { api } from "../api";

interface Device {
  _id: string;
  serial?: string;
  model?: string;
  code?: string;
  user?: {
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    identity?: "parent" | "child" | "mentor" | "mentee" | "self-managed" | "organization";
  };
}

interface ChildProfile {
  id?: string;
  _id?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  age?: number;
}

interface AuthContextType {
  code: string;
  setCode: (code: string) => void;
  device: Device | null;
  user: Device["user"] | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasError: boolean;
  selectedChild: ChildProfile | null;
  logout: () => void;
  selectChild: (child: ChildProfile) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [code, setCodeState] = React.useState<string>("");
  const [selectedChild, setSelectedChild] = React.useState<ChildProfile | null>(null);
  const [queryError, setQueryError] = React.useState<boolean>(false);

  // Wrap in try-catch for error handling
  let userInfo: any = undefined;
  try {
    userInfo = useQuery(api.users.get.fromCode, code.length === 6 && !queryError ? { code } : "skip");
  } catch (error) {
    console.error("Auth query error:", error);
    userInfo = null;
  }

  // Reset error state when code changes
  const setCode = (newCode: string) => {
    setQueryError(false);
    setCodeState(newCode);
  };

  // Handle query errors (e.g., device not found)
  React.useEffect(() => {
    if (userInfo === null && code.length === 6) {
      setQueryError(true);
    }
  }, [userInfo, code]);
  const user = userInfo ?? null;
  const isAuthenticated = Boolean(userInfo);
  const isLoading = code.length === 6 && userInfo === undefined && !queryError;
  const hasError = queryError;
  const device: Device | null = userInfo ? { _id: code, code, user: userInfo as any } : null;

  // Log code changes
  React.useEffect(() => {
    console.group("🔐 Authentication State - Code Changed");
    console.log("Code:", code);
    console.log("Code length:", code.length);
    console.log("Will trigger API call:", code.length === 6);
    console.log("Query params:", code.length === 6 ? { code } : "skip");
    console.groupEnd();
  }, [code]);

  // Log API query state and responses
  React.useEffect(() => {
    if (code.length === 6) {
      console.group("📡 Convex API Query State");
      console.log("Query params:", { code });
      console.log("userInfo value:", userInfo);
      console.log("Query status:", {
        isLoading: userInfo === undefined,
        hasData: userInfo !== undefined && userInfo !== null,
        hasNull: userInfo === null,
      });
      console.groupEnd();

      if (userInfo !== undefined) {
        if (userInfo === null) {
          console.group("❌ Authentication Failed");
          console.log("Invalid code:", code);
          console.log("API returned null - code not found or invalid");
          console.groupEnd();
        } else {
          console.group("✅ API Response Received");
          console.log("Raw userInfo:", userInfo);
          console.log("User data:", {
            firstName: (userInfo as any)?.firstName,
            lastName: (userInfo as any)?.lastName,
            email: (userInfo as any)?.email,
            phone: (userInfo as any)?.phone,
          });
          console.log("Device object:", device);
          console.log("Is Authenticated:", isAuthenticated);
          console.groupEnd();

          console.group("🎉 Authentication Successful");
          console.log("User logged in:", {
            firstName: (userInfo as any)?.firstName,
            lastName: (userInfo as any)?.lastName,
            email: (userInfo as any)?.email,
            phone: (userInfo as any)?.phone,
          });
          console.log("Device code:", code);
          console.groupEnd();

          if (userInfo.email) posthog.identify(userInfo.email);
        }
      }
    }
  }, [code, userInfo, device, isAuthenticated]);

  // Log loading state changes
  React.useEffect(() => {
    if (isLoading) {
      console.group("⏳ Loading State");
      console.log("Waiting for API response...");
      console.log("Code being verified:", code);
      console.groupEnd();
    }
  }, [isLoading, code]);

  const logout = () => {
    console.group("🚪 Logout");
    console.log("User logging out");
    console.log("Clearing code and selected child");
    console.groupEnd();
    setCode("");
    setSelectedChild(null);
  };

  const selectChild = (child: ChildProfile) => {
    setSelectedChild(child);
  };

  const value = {
    code,
    setCode,
    device,
    user,
    isAuthenticated,
    isLoading,
    hasError,
    selectedChild,
    logout,
    selectChild,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
